import React, { useEffect } from 'react'
import Input from '../Input'
import './style.scss'
import useGetValidationMessage from '../hooks/useGetValidationMessage'
import useResetValidationMessage from '../hooks/useResetValidationMessage'

interface IIntegerInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id?: string
  classNames?: string
  setValue?(value: number, label?: string): void
  label?: any
  level1FieldName?: string
  level2FieldName?: string
  fieldName?: string
  unit?: string | JSX.Element
  signed?: boolean
  placeholder?: string
  noEmpty?: boolean
  infoText?: string
  minValue?: number
  maxValue?: number
}

const IntegerInput: React.FC<IIntegerInputProps> = ({
  id,
  value,
  classNames,
  label,
  disabled,
  setValue,
  level1FieldName,
  level2FieldName,
  fieldName,
  unit,
  signed,
  placeholder = '—',
  noEmpty = false,
  minValue = -Infinity,
  maxValue = Infinity,
  infoText,
  ...props
}) => {
  const [valueState, setValueState] = React.useState<string>('')
  const valueRef = React.useRef<any>()
  const timerId = React.useRef<ReturnType<typeof setTimeout>>()
  const [resetValidation, setResetValidation] = React.useState<number>(0)

  const errorText = useGetValidationMessage(id, level1FieldName, level2FieldName, fieldName)
  useResetValidationMessage(resetValidation, id, level1FieldName, level2FieldName, fieldName)

  React.useEffect(() => {
    value === undefined || value === null || isNaN(value as number)
      ? setValueState('')
      : setValueState(value.toString())
  }, [value])

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorText) setResetValidation(resetValidation + 1)
    if (signed) {
      setValueState(
        e.target.value
          .replace(/,/, '.')
          .replace(/[^-?0-9]/g, '')
          .replace(/(\..*)\./g, '$1'),
      )
    } else
      setValueState(
        e.target.value
          .replace(/,/, '.')
          .replace(/[^0-9]/g, '')
          .replace(/(\..*)\./g, '$1'),
      )
    timerId.current && clearInterval(timerId.current)
    timerId.current = setInterval(async () => {
      updateValue()
    }, 2000)
  }

  const updateValue = () => {
    onBlurHandler()
    timerId.current && clearInterval(timerId.current)
  }
  useEffect(() => {
    valueRef.current = valueState
  }, [valueState])

  const onBlurHandler = () => {
    if (!valueRef.current?.length) {
      if (noEmpty) {
        setValueState(value!.toString())
      } else if (value && setValue) {
        setValue(NaN, fieldName)
      }
    } else {
      const newValue = Math.max(minValue, Math.min(maxValue, parseFloat(valueRef.current!)))
      if (setValue && newValue !== value) {
        setValue(newValue, fieldName)
      } else if (value !== undefined && value !== null) {
        setValueState(value.toString())
      }
    }
  }
  return (
    <Input
      {...props}
      label={label}
      unit={unit}
      size={valueState.toString().length | 1}
      value={valueState}
      onChange={onChangeHandler}
      classNames={classNames}
      disabled={disabled}
      onEnter={updateValue}
      placeholder={placeholder}
      infoText={infoText}
      errorText={errorText as string[]}
    />
  )
}

export default IntegerInput
